import { CloseReasonOption } from "@coworker/apprestructured/src/task-management/components/CloseSheet/CloseSheet";
import { useAllCloseReasons } from "@coworker/apprestructured/src/task-management/services/useAllCloseReasons";
import { TaskType } from "@coworker/apprestructured/src/task-management/types/TaskType";
import { useTranslation } from "@coworker/locales";
import { useMemo } from "react";

const findIntersectionOfCloseReasonIds = (
  tasksTypes: TaskType[]
): Set<string> => {
  if (tasksTypes.length === 1) {
    return new Set(tasksTypes[0]!.close_reason_ids);
  }

  return tasksTypes.reduce((acc, taskType, idx) => {
    const ids = new Set(taskType.close_reason_ids);
    if (idx === 0) {
      return ids;
    }
    return new Set([...acc].filter((id) => ids.has(id)));
  }, new Set<string>());
};

const NO_CLOSE_REASON_OPTIONS: CloseReasonOption[] = [];

export function useTasksCloseReasonOptions(
  tasksTypes: TaskType[],
  { enabled = true }: { enabled?: boolean } = {}
) {
  const { t } = useTranslation();
  const shouldFetch = enabled && tasksTypes.length > 0;
  const { data: allCloseReasons, isLoading } = useAllCloseReasons({
    enabled: shouldFetch,
  });

  const closeReasonOptions = useMemo(() => {
    if (!allCloseReasons || !tasksTypes.length) {
      return NO_CLOSE_REASON_OPTIONS;
    }

    // Find the intersection of close reason IDs across all task types
    const commonCloseReasonIds = findIntersectionOfCloseReasonIds(tasksTypes);

    return allCloseReasons
      .filter(({ id }) => commonCloseReasonIds.has(id))
      .map(({ id, label, requires_description: requiresDescription }) => ({
        value: id,
        label: t(label),
        requiresDescription,
      }));
  }, [allCloseReasons, t, tasksTypes]);

  return { closeReasonOptions, isLoading };
}
